import { X, TrendingUp, AlertTriangle, MapPin } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hours = (n) => `${Number(n || 0).toFixed(2)}h`;

/** Draft Analysis — admin-only spread on a draft report. Never printed. */
export default function DraftAnalysisPanel({ state, onClose }) {
  if (!state) return null;
  const a = state.data;

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
         data-testid="draft-analysis-panel">
      <div className="bg-white border-2 border-ink shadow-hard w-full max-w-4xl">
        <div className="flex items-start justify-between gap-4 border-b-2 border-ink p-4">
          <div>
            <div className="overline mb-1 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> § Draft Analysis · admin only
            </div>
            <div className="font-mono text-xs text-muted-foreground">
              {a ? `${a.worksite_name} · ${a.period_start} → ${a.period_end}` : "Computing spread…"}
            </div>
          </div>
          <button data-testid="close-draft-analysis" onClick={onClose}
                  className="border-2 border-ink p-1.5 hover:bg-ink hover:text-white"><X className="w-4 h-4 pointer-events-none" /></button>
        </div>

        {!a ? (
          <div className="p-8 font-mono text-xs text-muted-foreground">Loading…</div>
        ) : (
          <div className="p-4 sm:p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <Stat label="Billed to client" value={money(a.total_billed)} testid="analysis-billed" />
              <Stat label="Paid to employees" value={money(a.total_labour_cost)} testid="analysis-labour" />
              <Stat label="Projected profit" value={money(a.projected_profit)} testid="analysis-profit" highlight />
              <Stat label="Margin" value={`${Number(a.margin_pct || 0).toFixed(1)}%`} testid="analysis-margin" />
            </div>

            <div className="font-mono text-[11px] text-muted-foreground border-l-2 border-ink pl-3">
              Rate in force <b data-testid="analysis-rate">{money(a.billable_rate)}/h</b>
              {" "}({a.rate_source === "run_override" ? "per-client rate, this report only" : "worksite standing rate"}).
              Employer FICA estimate <b data-testid="analysis-fica">{money(a.employer_fica_estimate)}</b> →
              profit after FICA <b data-testid="analysis-profit-after-fica">{money(a.projected_profit_after_fica)}</b>.
            </div>

            <div className="overflow-x-auto">
              <table className="w-full font-mono text-xs" data-testid="analysis-table">
                <thead className="bg-ink text-white">
                  <tr>
                    <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Employee</th>
                    <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Hours</th>
                    <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Billed</th>
                    <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Paid</th>
                    <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Spread</th>
                    <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Client location</th>
                  </tr>
                </thead>
                <tbody>
                  {a.rows.map((r) => (
                    <tr key={r.employee_id} className="border-t border-ink/20"
                        data-testid={`analysis-row-${r.employee_id}`}>
                      <td className="px-3 py-2 font-semibold">{r.employee_name}</td>
                      <td className="px-3 py-2 text-right">{hours(r.hours)}</td>
                      <td className="px-3 py-2 text-right">{money(r.billed)}</td>
                      <td className="px-3 py-2 text-right">
                        {money(r.labour_cost)}
                        <div className="text-[10px] text-muted-foreground">{money(r.pay_rate)}/h · {r.pay_rate_basis}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-bold">{money(r.spread)}</td>
                      <td className="px-3 py-2">
                        {r.locations.map((l) => (
                          <div key={l.location} className="flex items-center gap-1 text-[11px]">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className={l.location === "UNPINNED" ? "font-black" : ""}>{l.location}</span>
                            <span className="text-muted-foreground">· {hours(l.hours)}</span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {a.fully_pinned ? (
              <div className="border-2 border-ink p-3 font-mono text-[11px] flex items-center gap-2"
                   data-testid="analysis-pinning-ok">
                <MapPin className="w-4 h-4" /> Every line item is pinned to a client location — zero gaps.
              </div>
            ) : (
              <div className="border-2 border-ink bg-warn p-3 font-mono text-[11px]"
                   data-testid="analysis-pinning-gap">
                <div className="flex items-center gap-2 font-black mb-2">
                  <AlertTriangle className="w-4 h-4" /> {a.unpinned_entries.length} line item(s) are not pinned to this client location
                </div>
                {a.unpinned_entries.slice(0, 8).map((u, i) => (
                  <div key={`${u.entry_id}-${i}`}>
                    {u.employee_name} · {hours(u.hours)} · {u.reason}
                  </div>
                ))}
              </div>
            )}

            <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">{a.note}</div>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, testid, highlight }) => (
  <div className={"border-2 border-ink p-3 " + (highlight ? "bg-ink text-white" : "")}>
    <div className={"font-mono text-[10px] uppercase tracking-widest " + (highlight ? "text-white/60" : "text-muted-foreground")}>{label}</div>
    <div className="font-mono font-black text-lg mt-1" data-testid={testid}>{value}</div>
  </div>
);
